import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import type {
  ListingsAPIResponseNumericAttribute,
  ListingsAPIResponsePagination,
  ListingsAPIResponseProduct,
} from '@noths/polaris-client-listings-api';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { deliveryZoneCodes } from '@noths/polaris-client-localisation';
import type { CurrencyCode, Navigation } from '@noths/polaris-dev-ts-types';

import { DeliveryCost } from 'src/featureFlags/featureFlags';
import type { RequestProductsActionProductsPayload } from 'src/redux/products/types';
import type { Product } from 'src/redux/products/types/Product';

interface AdaptResponseProductsToStoreArgs {
  currencyCode: CurrencyCode;
  deliveryZone: DeliveryZoneCode;
  featureFlags: FeatureFlags;
  metadata: { attributionToken: string; filter?: string };
  pagination: ListingsAPIResponsePagination;
  products: ListingsAPIResponseProduct[];
  term: string;
}

export const adaptResponseProductsToStore = ({
  currencyCode,
  deliveryZone,
  featureFlags,
  metadata,
  pagination,
  products,
  term,
}: AdaptResponseProductsToStoreArgs): RequestProductsActionProductsPayload => {
  const category = null;
  const navigation: Navigation[] = [];
  const providerMetadata = metadata.filter ? { filter: metadata.filter } : null;
  const partner = null;
  const providerAttributionToken = metadata.attributionToken || null;

  const {
    currentPage,
    pageSize: perPage,
    resultsReturnedThisPage: productsReturnedCount,
    totalResults: totalProducts,
  } = pagination;

  const totalPages = Math.ceil(totalProducts / perPage);

  const adaptedProducts = products.map((product) => {
    const {
      attributes,
      freeDomesticDelivery,
      images,
      isSponsored,
      links,
      lowCommitmentProductFlag,
      lowCommitmentProductType,
      ...restOfProduct
    } = product;

    const isMainlandUKDeliveryZone = deliveryZone === deliveryZoneCodes[1];

    let deliveryCost = null;
    let deliveryDays = null;
    if (featureFlags[DeliveryCost.NAME] === DeliveryCost.ENABLE) {
      const deliveryFreeDays = isMainlandUKDeliveryZone
        ? (attributes?.delivery_days_FREE as ListingsAPIResponseNumericAttribute)?.numbers?.[0] ??
          null
        : null;
      const standardDeliveryCost = isMainlandUKDeliveryZone
        ? (attributes?.delivery_cost_STANDARD as ListingsAPIResponseNumericAttribute)
            ?.numbers?.[0] ?? null
        : null;
      const standardDeliveryDays = isMainlandUKDeliveryZone
        ? (attributes?.delivery_days_STANDARD as ListingsAPIResponseNumericAttribute)
            ?.numbers?.[0] ?? null
        : null;
      if (deliveryFreeDays !== null) {
        deliveryCost = 0;
        deliveryDays = deliveryFreeDays;
      } else {
        deliveryCost = standardDeliveryCost;
        deliveryDays = standardDeliveryDays;
      }
    } else {
      deliveryDays = isMainlandUKDeliveryZone
        ? (attributes?.delivery_days as ListingsAPIResponseNumericAttribute)?.numbers?.[0] ?? null
        : null;
    }

    return {
      ...restOfProduct,
      detailPageHref: links?.[0]?.rel || '',
      imageSrc: images?.[0]?.href || '',
      deliveryCost,
      deliveryDays,
    } as Product;
  });

  const result = {
    category,
    currencyCode,
    products: adaptedProducts,
    perPage,
    lastPageLoaded: currentPage,
    totalPages,
    totalProducts,
    productsReturnedCount,
    term,
    navigation,
    partner,
    providerAttributionToken,
    providerMetadata,
  };
  return result;
};
